import { Common, device, Profile, color } from '@stockbit/ui';
import Image from 'global/Image';
import styled, { css } from 'styled-components';

const {
  Button,
  Card,
  Flex,
  Input,
  Text,
  Checkbox: BaseCheckbox,
  Modal: ModalAntd,
} = Common;
const {
  ProfileSwitch: Switch,
  ConfigNavigation,
  ConfigNavigationItem,
} = Profile;

const LeftNavigation = styled(Flex)`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 40px;
`;

const Frame = styled(Flex).attrs({
  direction: 'column',
  align: 'center',
})`
  width: 180px;
  border: solid 1px ${color.gainsboro};
`;

const Avatar = styled(Image)`
  width: 180px;
  height: 180px;
  background: ${color.mischka};
`;

const CardHeader = styled(Flex)`
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  background: ${color.light};
  padding: 15px 29px;
  border: 1px solid ${color.gainsboro};
  border-bottom: none;
  border-radius: 2px;
  width: 100%;

  img {
    display: block;
  }
`;

const CardHeaderTextContainer = styled(Flex)`
  flex-direction: column;
  flex: 1;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  margin-left: 10px;
`;

const CardHeaderTitle = styled(Text).attrs({ size: '18px' })``;

const CardHeaderExtra = styled(Text).attrs({
  size: '14px',
  align: 'right',
  color: `${color.primary}`,
})``;

const IconMoreWrapper = styled(Flex)`
  justify-content: 'center';
  align-items: 'flex-end';
  width: 30px;
  height: 40px;
  display: flex;
  transform: rotate(90deg);

  @media ${device.tablet} {
    display: none;
  }
`;

const NavigationItem = styled(Flex).attrs(
  (props: { isActive: boolean; height: string }) => ({
    isActive: props.isActive || false,
    height: props.height || '40px',
  }),
)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border: 1px solid ${color.gainsboro};
  border-bottom: 0 solid;
  transition: background 0.3s;
  background: ${color.light};
  cursor: pointer;
  position: relative;
  height: ${({ height }) => height};

  &:last-of-type {
    border-radius: 0 0 3px 3px;
    border-bottom: 1px solid ${color.gainsboro};
  }
`;

const ProfileLabel = styled.label.attrs({
  className: 'control-label',
})`
  margin-right: 16px;
  min-width: 150px;
  color: ${color.highempahis};
  text-align: start;

  @media ${device.tablet} {
    text-align: end;
  }
`;

const ProfileInputGroup = styled(Flex).attrs((props) => ({
  isFluid: props.isFluid || false,
  align: props.align || 'center',
  className: 'control-group',
}))`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-bottom: 24px;
  ${({ isFluid }) =>
    isFluid &&
    css`
      width: 100%;
    `}

  .ant-input-group-wrapper {
    border: none !important;
  }

  @media ${device.tablet} {
    align-items: flex-start;
    flex-direction: row;
  }
`;

const RadioButton = styled.input`
  display: none;
  :checked + label {
    border-radius: 4px;
    border: 2px solid ${color.primary};
  }
`;

const RegionFlag = styled(Image)`
  width: 40px;
  display: block;
  position: relative;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: ${color.highempahis};
`;

const SectionLabel = styled.span`
  margin: 0 8px 2px;
  font-size: 12px;
  font-weight: 400;
  color: ${color.primary};
  padding: 16px 0px 12px !important;
  width: calc(100% - 16px) !important;
`;

export const NotificationWrapper = styled(Flex)`
  flex-direction: column;
  width: 100%;
  padding: 10px 8px;
  border: none !important;
`;

const ProfileSwitch = styled(Switch)`
  ${({ checked }) =>
    checked &&
    css`
      background-color: ${color.primary};
    `}
`;

const CustomNavigationItem = styled(ConfigNavigationItem)`
  justify-content: space-between;
  width: 100%;
  padding: 24px 20px;
  border-right: 1px solid ${color.gainsboro};
  align-items: center;

  :hover {
    border-right: 1px solid ${color.gainsboro};
  }

  &:last-of-type {
    border-radius: 0 0 3px 3px !important;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      border-right: 3px solid ${color.primary} !important;
      background: ${color.aliceBlue};
    `}
`;

const NavigationHeader = styled(Flex)`
  align-items: center;
  padding: 20px;
  border: 1px solid ${color.gainsboro};
  border-bottom: 0 solid;
  background: ${color.light};
  width: 100%;
`;

const CustomConfigNavigation = styled(ConfigNavigation)`
  margin: 0;
  width: 100%;
`;

const EditProfileCard = styled(Card)`
  .ant-card-head-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin: 0 -5px;
  }

  .ant-card-head {
    border: ${(props) => (props.isHideTitle ? 'none' : '1px solid #f0f0f0')};
  }

  .ant-card-head-title {
    font-size: 16px;
    color: ${(props) =>
      props.isHideTitle ? `${color.light}` : `${color.primary}`};
    padding: 0;
  }

  .ant-card-extra {
    font-size: 14px;
    margin-left: 0px;
    padding: 0px;
  }

  .ant-btn-primary[disabled] {
    background: #8cd9bc !important;
    color: #ffffff !important;
  }

  @media ${device.tablet} {
    .ant-card-head-wrapper {
      display: flex;
      flex-direction: row;
      padding: 16px 5px;
    }

    .ant-card-head-title {
      margin: 0;
      padding: 0;
    }

    .ant-card-extra {
      font-size: 16px;
      margin: 0;
      padding: 0;
    }
  }

  @media ${device.laptop} {
    .ant-card-extra {
      margin: 0;
      padding: 0;
    }
  }
`;

const PrivacySectionTitle = styled(Flex).attrs({ width: '100%', py: '20px' })`
  border-bottom: 1px solid ${color.downgray};
`;

const Checkbox = styled(BaseCheckbox)`
  .ant-checkbox-inner {
    border-radius: 100% !important;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background: ${color.primary};
    border-color: ${color.primary}!important;
  }
`;

const TogglePassword = styled(Image)`
  margin-left: -25px;
  margin-top: 8px;
  z-index: 99;
  width: 15px;
  cursor: pointer;
`;

const PasswordSubmitButton = styled(Button)`
  min-width: 95px;
  height: 35px;
  border-radius: 2px;
  border: none;
  font-family: var(--bold-font);
  background: ${(props) =>
    props.disabled ? `${color.primary} !important` : `${color.primary}`};
  color: ${(props) =>
    props.disabled ? `${color.light} !important` : `${color.light}`};
  margin-left: 10px;
`;

const PasswordCancelButton = styled(Button)`
  border: none !important;
  min-width: 95px;
  height: 35px;
  font-family: var(--bold-font);
  background: ${color.light} !important;
  color: ${color.midgray} !important;
  box-shadow: none;
`;

const InputPassword = styled(Input).attrs({ autoComplete: 'new-password' })`
  border: 1px solid
    ${(props) => (props.error ? `${color.sunsetOrange}` : `${color.gainsboro}`)} !important;
`;

const EditProfileContainer = styled(Flex)`
  flex-direction: column;
  width: calc(100% - 210px);

  .ant-btn-primary[disabled] {
    background: #8cd9bc !important;
    color: #ffffff !important;

    &:hover > p {
      color: #ffffff;
    }
  }

  button.ant-btn {
    box-shadow: none;
    margin-right: 10px;
  }

  & > div {
    border-radius: 3px;
  }

  @media ${device.laptop} {
    width: 530px;
  }
`;

const Modal = styled(ModalAntd)`
  .ant-modal-header {
    padding: 0 20px !important;
    height: 40px;
    border-bottom: solid 1px ${color.gainsboro};

    ${({ noBorder }) =>
      noBorder &&
      css`
        border: none;
      `}
  }
  .ant-modal-body {
    padding: 20px;
  }
  .ant-modal-footer {
    padding: 0 !important;
    text-align: left;
    display: flex;

    button {
      border-radius: 3px;
    }
  }
  .ant-modal-close-x {
    width: 40px;
    height: 40px;
    line-height: 40px;
    font-size: 12px;
  }
`;

export {
  CustomConfigNavigation,
  LeftNavigation,
  CardHeader,
  CardHeaderTextContainer,
  CardHeaderTitle,
  CardHeaderExtra,
  IconMoreWrapper,
  Frame,
  Avatar,
  NavigationItem,
  ProfileLabel,
  ProfileInputGroup,
  RadioButton,
  RegionFlag,
  CheckboxLabel,
  SectionLabel,
  ProfileSwitch,
  CustomNavigationItem,
  NavigationHeader,
  EditProfileCard,
  PrivacySectionTitle,
  Checkbox,
  TogglePassword,
  PasswordSubmitButton,
  PasswordCancelButton,
  InputPassword,
  EditProfileContainer,
  Modal,
};
